import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  deleteMediaMessage,
  getPosts,
  updateMedia,
  updatePost,
} from "../../actions/post.actions";
import { UidContext } from "../App.Context";
import { DateParser, isEmpty } from "../utils/Utils";
import CardComments from "./CardComments";
import DeleteCard from "./DeleteCard";
import Like from "./Like";
import Editor from "ckeditor5-custom-build/build/ckeditor";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import { S3UploadAdapterPlugin } from "./CKEditorUploadAdapter";
import StatusMessage from "./StatusMessage";
import StatusResolve from "./StatusResolve";
import parse from "html-react-parser";
import ImageProfil from "../Profil/ImageProfil";
import ImagePost from "./ImagePost";
import { toast } from "react-toastify";
import StatusUpdate from "./StatusUpdate";
import StatusTuto from "./StatusTuto";
import StatusInformation from "./StatusInformation";
import StatusNews from "./StatusNews";

const POST_COLLAPSED_HEIGHT = 260;

const Card = ({ post, socket }) => {
  const [isUpdated, setIsUpdated] = useState(false);
  const [textUpdate, setTextUpdate] = useState(null);
  const [showComments, setShowComments] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [needsTruncation, setNeedsTruncation] = useState(false);
  const postContentRef = React.useRef(null);

  const uid = useContext(UidContext);
  const [isAdmin, setIsAdmin] = useState(false);
  const [mediaFront, setMediaFront] = useState([]);
  const [media, setMedia] = useState([]);
  const [sousTraitant, setSousTraitant] = useState(null);
  const [existingImgUrls, setExistingImgUrls] = useState([]); // For edit mode

  const usersData = useSelector((state) => state.usersReducer);
  const userData = useSelector((state) => state.userReducer);
  const commentsData = useSelector((state) => state.commentReducer);
  const dispatch = useDispatch();

  const author = usersData.find((user) => user.id === post.author);
  const authorName = `${author?.name} ${author?.firstname}`;

  // Fetch existing images when entering edit mode
  useEffect(() => {
    if (isUpdated && post.media) {
         axios.get(`${process.env.REACT_APP_API_URL}/api/message/image/${post.id}`, { withCredentials: true })
            .then(res => {
                if(Array.isArray(res.data)) {
                    setExistingImgUrls(res.data);
                } else if (typeof res.data === 'string') {
                    setExistingImgUrls([res.data]);
                }
            })
            .catch(err => console.error("Error fetching images for edit", err));
    }
  }, [isUpdated, post.media, post.id]);

  const updateItem = () => {
    if (textUpdate && !sousTraitant) {
      dispatch(updatePost(post.id, textUpdate));
      dispatch(getPosts());
    } else if (textUpdate && sousTraitant) {
      dispatch(updatePost(post.id, textUpdate, sousTraitant));
      dispatch(getPosts());
    }
    setIsUpdated(false);
  };

  const updateImg = () => {
    const formData = new FormData();
    if (media && media.length > 0) {
        media.forEach(file => {
            formData.append("media", file);
        });
    }
    dispatch(updateMedia(post.id, formData)).then(() => dispatch(getPosts()));
    setIsUpdated(false);
    setMedia([]);
    setMediaFront([]);
  };

  const deleteImg = () => {
    // Legacy "Delete All"
    dispatch(deleteMediaMessage(post.id, {})).then(() =>
      dispatch(getPosts())
    );
    setIsUpdated(false);
  };

  const handleDeleteOneImg = (urlToDelete) => {
      // Optimistic update
      setExistingImgUrls(prev => prev.filter(u => u !== urlToDelete));
      
      dispatch(deleteMediaMessage(post.id, { urlToDelete }))
        .then(() => {
             // Dispatch getPosts to be sure? 
             // Or rely on local state update which we just did.
             // If we want to stay in edit mode, local state is enough.
             // But if we exit, `post.media` needs to be fresh.
             dispatch(getPosts());
        });
  };

  const handlePicture = (e) => {
    if (e.target.files && e.target.files.length > 0) {
        const fileArray = Array.from(e.target.files);
        setMedia(prev => [...prev, ...fileArray]);
        
        const urlArray = fileArray.map(file => URL.createObjectURL(file));
        setMediaFront(prev => [...prev, ...urlArray]);
    }
  };

  // Check admin
  useEffect(() => {
    if (uid === userData.id && userData.isAdmin) {
      setIsAdmin(true);
    }
  }, [uid, userData.id, userData.isAdmin]);

  // Measure post content height to determine if "Voir plus" is needed
  useEffect(() => {
    if (postContentRef.current && !isUpdated) {
      const height = postContentRef.current.scrollHeight;
      setNeedsTruncation(height > POST_COLLAPSED_HEIGHT);
    }
  }, [post.texte, isUpdated]);

  const numbComm = Object.keys(commentsData).reduce((count, id) => {
    if (commentsData[id].postId === post.id) {
      return count + 1;
    } else {
      return count;
    }
  }, 0);

  const handleCopy = () => {
    const url = `${process.env.REACT_APP_API_URL}/post/${post.id}`;
    navigator.clipboard
      .writeText(url)
      .then(() => toast.success("Url du post copié"));
  };

  return (
    <div
      className={`card-container ${post.status >= 1 && post.status <= 6 ? "has-status-bar" : ""}`}
      key={post.id}
    >
      <>
        <div className="cardHeader">
          <div className="cardHeader-img">
            <Link
              to={`/userprofil/${
                usersData.find((user) => user?.id === post?.author)?.id
              }`}
            >
              <ImageProfil
                id={usersData.find((user) => user?.id === post?.author)?.id}
              />
            </Link>
          </div>
          <div className="cardHeader-name">
            <span className="name">
              {!isEmpty(usersData[0]) &&
                usersData
                  .map((user) =>
                    user.id === post.author
                      ? user.firstname + " " + user.name
                      : null
                  )
                  .join("")}
            </span>
            <span className="service">
              {!isEmpty(usersData[0]) &&
                usersData
                  .map((user) =>
                    user.id === post.author ? user.service : null
                  )
                  .join("")}
            </span>
            <span className="date">{DateParser(post?.createdAt)}</span>
          </div>
          <div className="button-container-edit">
            <div className="share_post">
              <i
                className="fas fa-share-alt"
                onClick={handleCopy}
                title="partager le post"
              ></i>
            </div>
            {isAdmin || userData?.id === post?.author ? (
              <div className="cardheader-container-edit">
                <div
                  className="cardheader-edit"
                  onClick={() => setIsUpdated(!isUpdated)}
                >
                  <i className="far fa-edit"></i>
                </div>
              </div>
            ) : null}

            {isAdmin || userData?.id === post?.author ? (
              <div className="cardheader-container-delete">
                <DeleteCard id={post?.id} />
              </div>
            ) : null}
          </div>
        </div>
        <div className="status-container">
          {post.status === 1 ? <StatusMessage /> : null}
          {post.status === 2 ? <StatusResolve /> : null}
          {post.status === 3 ? <StatusUpdate /> : null}
          {post.status === 4 ? <StatusTuto /> : null}
          {post.status === 5 ? <StatusNews /> : null}
          {post.status === 6 ? <StatusInformation /> : null}
        </div>
        <div className="postContent">
          <div className="postContent-post">
            {isUpdated === false && (
              <>
                <div
                  ref={postContentRef}
                  className={`post-text-wrapper ${needsTruncation && !isExpanded ? "post-text-collapsed" : ""}`}
                  style={needsTruncation && !isExpanded ? { maxHeight: POST_COLLAPSED_HEIGHT + "px" } : {}}
                >
                  {parse(post?.texte || "")}
                </div>
                {needsTruncation && (
                  <div
                    className="post-toggle-expand"
                    onClick={() => setIsExpanded(!isExpanded)}
                  >
                    {isExpanded ? (
                      <span><i className="fas fa-chevron-up"></i> Voir moins</span>
                    ) : (
                      <span><i className="fas fa-chevron-down"></i> Voir plus</span>
                    )}
                  </div>
                )}
              </>
            )}
            {isUpdated && (
              <div className="update-post">
                <CKEditor
                  editor={Editor}
                  data={post?.texte || ""}
                  config={{
                    extraPlugins: [S3UploadAdapterPlugin],
                    link: {
                      defaultProtocol: "https://",
                      decorators: {
                        openInNewTab: {
                          mode: "automatic",
                          callback: () => true,
                          attributes: {
                            target: "_blank",
                            rel: "noopener noreferrer",
                          },
                        },
                      },
                    },
                    image: {
                      toolbar: [
                        "imageStyle:block",
                        "imageStyle:side",
                        "|",
                        "imageResize",
                        "|",
                        "toggleImageCaption",
                        "imageTextAlternative",
                      ],
                      insert: { type: "block" },
                      resizeUnit: "%",
                      resizeOptions: [
                        { name: "imageResize:original", value: null, label: "Originale" },
                        { name: "imageResize:25", value: "25", label: "25%" },
                        { name: "imageResize:50", value: "50", label: "50%" },
                        { name: "imageResize:75", value: "75", label: "75%" },
                      ],
                    },
                  }}
                  onChange={(event, editor) => {
                    const data = editor.getData();
                    setTextUpdate(data);
                  }}
                />
                <div className="button-container">
                  <div
                    className="sous-traitant-checkbox"
                    title="envoyer par mail au sous-traitant"
                  >
                    <input
                      type="checkbox"
                      id="sous-traitant-checkbox"
                      name="sous-traitant"
                      onChange={(e) => setSousTraitant(e.target.checked)}
                    />
                    <i className="fas fa-users"></i>
                  </div>
                  <button className="btn" onClick={updateItem}>
                    Modifier mon message
                  </button>
                </div>
              </div>
            )}
          </div>

          {isUpdated === false && (
            <div className="postContent-image">
              {post.media && <ImagePost id={post.id} />}
            </div>
          )}
          {isUpdated && (
            <div className="update-post">
              <div className="postContent-image">
                <div className="imageArea" style={{ background: '#242526', padding: '15px', borderRadius: '8px', border: '1px solid #3e4042', marginBottom: '15px' }}>
                   {existingImgUrls.length > 0 ? (
                       <>
                           <h5 style={{ fontSize: '0.9rem', color: '#E4E6EB', marginBottom: '10px', fontWeight: '600' }}>Images en ligne</h5>
                           <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: '10px' }}>
                                {existingImgUrls.map((url, idx) => (
                                    <div key={idx} style={{ position: 'relative', aspectRatio: '1', borderRadius: '6px', overflow: 'hidden', boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.3)' }}>
                                        <img src={url} alt="existing" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        <div 
                                            onClick={() => handleDeleteOneImg(url)}
                                            title="Supprimer"
                                            style={{
                                                position: 'absolute', top: '4px', right: '4px', 
                                                background: 'rgba(220, 38, 38, 0.9)', color: 'white', 
                                                borderRadius: '4px', width: '24px', height: '24px', 
                                                display: 'flex', justifyContent: 'center', alignItems: 'center', 
                                                cursor: 'pointer', transition: 'background 0.2s',
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.4)',
                                                border: '1px solid rgba(255,255,255,0.2)'
                                            }}>
                                            <i className="fas fa-trash-alt" style={{ fontSize: '12px' }}></i>
                                        </div>
                                    </div>
                                ))}
                           </div>
                       </>
                   ) : (
                       <p style={{ fontSize: '0.9rem', color: '#B0B3B8', margin: 0, fontStyle: 'italic' }}>Aucune image en ligne</p>
                   )}
                </div>

                {/* NEW IMAGES SECTION */}
                <div style={{ padding: '0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px', flexWrap: 'wrap', gap: '10px' }}>
                         <label htmlFor="file-upload" className="btn" style={{ 
                             cursor: 'pointer', margin: 0, fontSize: '0.9rem', padding: '8px 16px', 
                             display: 'inline-flex', alignItems: 'center', gap: '8px', 
                             background: '#3a3b3c', color: '#E4E6EB', border: '1px solid #3e4042',
                             borderRadius: '6px'
                         }}>
                            <i className="fas fa-plus-circle" style={{ color: '#E4E6EB' }}></i> Ajouter des photos
                         </label>
                         <input
                          type="file"
                          id="file-upload"
                          name="media"
                          multiple
                          accept=".jpg, .jpeg, .png"
                          onChange={(e) => handlePicture(e)}
                          style={{ display: 'none' }}
                        />
                        
                        {/* Global Delete Button */}
                        {post.media && existingImgUrls.length > 0 && (
                          <button
                            type="button"
                            style={{ 
                                background: 'transparent', color: '#ef4444', border: '1px solid #7f1d1d', 
                                padding: '6px 12px', fontSize: '0.8rem', borderRadius: '20px', 
                                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' 
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = '#450a0a'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                            onClick={(e) => {
                              if(window.confirm("Voulez-vous vraiment supprimer TOUTES les images de ce post ?")) {
                                  setMedia([]);
                                  deleteImg();
                              }
                            }}
                          >
                            <i className="fas fa-trash"></i> Tout supprimer
                          </button>
                        )}
                    </div>

                    {/* NEW MEDIA PREVIEW GRID */}
                    {mediaFront && mediaFront.length > 0 && (
                        <div style={{ background: '#0f291e', padding: '15px', borderRadius: '8px', border: '1px solid #14532d', marginTop: '10px' }}>
                             <h5 style={{ fontSize: '0.9rem', color: '#4ade80', marginBottom: '10px', fontWeight: '600' }}>Nouvelles sélections</h5>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: '10px' }}>
                                {mediaFront.map((url, idx) => (
                                     <div key={idx} style={{ position: 'relative', aspectRatio: '1', borderRadius: '6px', overflow: 'hidden' }}>
                                        <img src={url} alt="New preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                     </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
              </div>

              <div className="button-container">
                {media && media.length > 0 && (
                  <button className="btn" onClick={updateImg} style={{ width: '100%', background: '#faa61a', color: 'white', padding: '8px', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
                    <i className="fas fa-save" style={{ marginRight: '8px' }}></i>
                    Enregistrer les changements d'images
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
        <div className="cardFooter">
          <Like post={post} socket={socket} authorName={authorName} />
          <div className="footer-right">
            <div
              className="cardfooter-comment"
              onClick={() => setShowComments(!showComments)}
            >
              <i className="far fa-comment-alt"></i>
              <span>
                {numbComm} {numbComm > 1 ? "commentaires" : "commentaire"}
              </span>
            </div>
          </div>
        </div>
        {showComments && (
          <CardComments post={post} socket={socket} authorName={authorName} />
        )}
      </>
    </div>
  );
};

export default Card;
